import { useRef, useState } from 'react'
import { ArrowLeft, Mail } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import AuthLayout from '@/components/AuthLayout'
import { api } from '@/lib/api'

export default function VerifyEmail() {
  const navigate = useNavigate()
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])

  const [step, setStep] = useState<'email' | 'code'>('email')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isValidEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  }

  const handleSendCode = async () => {
    setError('')
    setSuccess('')

    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
        setError('И-мэйл хаяг оруулна уу')
        return
    }

    if (!isValidEmail(trimmedEmail)) {
        setError('Зөв и-мэйл хаяг оруулна уу')
        return
    }

    setLoading(true)

    try {
        const result = await api.auth.sendVerifyEmailCode(trimmedEmail)

        setSuccess(result.message)

        // Аль хэдийн баталгаажсан бол OTP input руу орохгүй
        if (result.message === 'Таны имэйл аль хэдийн баталгаажсан байна') {
        return
        }

        setStep('code')

        setTimeout(() => {
        inputRefs.current[0]?.focus()
        }, 100)
    } catch (err: any) {
        setError(err.message || 'Код илгээх үед алдаа гарлаа')
    } finally {
        setLoading(false)
    }
   }

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)

    const newCode = [...code]
    newCode[index] = digit
    setCode(newCode)

    setError('')
    setSuccess('')

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleCodeKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace') {
      if (code[index]) {
        const newCode = [...code]
        newCode[index] = ''
        setCode(newCode)
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (e.key === 'Enter') {
      handleVerify()
    }
  }

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault()

    const pastedCode = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6)

    if (!pastedCode) return

    const newCode = ['', '', '', '', '', '']

    pastedCode.split('').forEach((digit, index) => {
      newCode[index] = digit
    })

    setCode(newCode)

    const nextIndex = Math.min(pastedCode.length, 5)
    inputRefs.current[nextIndex]?.focus()
  }

  const handleVerify = async () => {
    setError('')
    setSuccess('')

    const verificationCode = code.join('')

    if (verificationCode.length !== 6) {
      setError('6 оронтой баталгаажуулах код оруулна уу')
      return
    }

    setLoading(true)

    try {
      const result = await api.auth.verifyOtp({
        email: email.trim(),
        code: verificationCode,
      })

      localStorage.setItem('token', result.token)

      navigate('/')
    } catch (err: any) {
      setError(err.message || 'И-мэйл баталгаажуулах үед алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setStep('email')
    setCode(['', '', '', '', '', ''])
    setError('')
    setSuccess('')
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-[550px] flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-black">
            И-мэйл баталгаажуулах
          </h1>

          <p className="text-sm text-gray-600">
            {step === 'email'
              ? 'Бүртгэлтэй и-мэйл хаягаа оруулж баталгаажуулах код авна уу.'
              : `${email} хаяг руу илгээсэн 6 оронтой кодыг оруулна уу.`}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-600 text-sm rounded-xl px-4 py-3">
            {success}
          </div>
        )}

        {step === 'email' ? (
          <>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                И-мэйл хаяг
              </label>

              <div
                className={`bg-white border rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 transition ${
                  error
                    ? 'border-red-400'
                    : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                }`}
              >
                <Mail className="size-[18px] text-gray-400 shrink-0" />

                <input
                  type="email"
                  placeholder="info@gmail.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSendCode()
                    }
                  }}
                  className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
                />
              </div>
            </div>

            <button
              onClick={handleSendCode}
              disabled={loading}
              className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50"
            >
              {loading ? 'Код илгээж байна...' : 'Баталгаажуулах код авах'}
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              <label className="text-sm font-medium text-gray-700">
                Баталгаажуулах код
              </label>

              <div className="flex justify-between gap-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(element) => {
                      inputRefs.current[index] = element
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) =>
                      handleCodeChange(index, e.target.value)
                    }
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-full h-14 rounded-xl border border-gray-200 bg-white text-center text-xl font-semibold text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                ))}
              </div>
            </div>

            <button
              onClick={handleVerify}
              disabled={loading}
              className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50"
            >
              {loading ? 'Баталгаажуулж байна...' : 'И-мэйл баталгаажуулах'}
            </button>

            <button
              type="button"
              onClick={handleSendCode}
              disabled={loading}
              className="text-sm font-medium text-primary-500 hover:text-primary-600 disabled:opacity-50"
            >
              Код дахин илгээх
            </button>

            <button
              type="button"
              onClick={handleBack}
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              И-мэйл хаяг өөрчлөх
            </button>
          </>
        )}

        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="size-5" />
          Нэвтрэх хуудас руу буцах
        </Link>
      </div>
    </AuthLayout>
  )
}
